import {
  Bike,
  Briefcase,
  Building2,
  CarFront,
  DoorOpen,
  Hammer,
  HardHat,
  KeyRound,
  Package,
  ShieldCheck,
  ShoppingBag,
  Truck,
  UserPlus,
  UserRound,
  Users,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import type { MemberAccessEntryTypeOption } from '@features/access-invites/owner/api';

/** Lucide class names — keep in sync with tenant-api accessEntryTypePortalIcons.ts */
export const ACCESS_ENTRY_TYPE_PORTAL_ICON_CLASSES = [
  'DoorOpen',
  'HardHat',
  'Package',
  'UserRound',
  'KeyRound',
  'Truck',
  'CarFront',
  'Users',
  'Briefcase',
  'Wrench',
  'UserPlus',
  'Building2',
  'ShoppingBag',
  'Hammer',
  'ShieldCheck',
  'Bike',
] as const;

export type AccessEntryTypePortalIconClass = (typeof ACCESS_ENTRY_TYPE_PORTAL_ICON_CLASSES)[number];

const ICON_BY_CLASS: Record<AccessEntryTypePortalIconClass, LucideIcon> = {
  DoorOpen,
  HardHat,
  Package,
  UserRound,
  KeyRound,
  Truck,
  CarFront,
  Users,
  Briefcase,
  Wrench,
  UserPlus,
  Building2,
  ShoppingBag,
  Hammer,
  ShieldCheck,
  Bike,
};

export const DEFAULT_ACCESS_ENTRY_TYPE_ICON = DoorOpen;

type EntryTypeLike = Pick<MemberAccessEntryTypeOption, 'portal_icon_class' | 'code' | 'name'> | null | undefined;

function isPortalIconClass(value: string): value is AccessEntryTypePortalIconClass {
  return (ACCESS_ENTRY_TYPE_PORTAL_ICON_CLASSES as readonly string[]).includes(value);
}

/** Name/code heuristic when portal_icon_class is not configured yet. */
function heuristicAccessEntryTypeIcon(type: EntryTypeLike): LucideIcon {
  const key = `${type?.code ?? ''} ${type?.name ?? ''}`.toLowerCase();
  if (key.includes('contractor')) return HardHat;
  if (key.includes('deliver')) return Package;
  if (key.includes('domestic')) return UserRound;
  return DEFAULT_ACCESS_ENTRY_TYPE_ICON;
}

/** Resolve the Lucide icon for an access entry type (configured class, then heuristic). */
export function resolveAccessEntryTypeIcon(type: EntryTypeLike): LucideIcon {
  const configured = type?.portal_icon_class?.trim();
  if (configured && isPortalIconClass(configured)) {
    return ICON_BY_CLASS[configured];
  }
  return heuristicAccessEntryTypeIcon(type);
}

export function buildAccessEntryTypeIconMap(
  entryTypes: MemberAccessEntryTypeOption[],
): Map<string, LucideIcon> {
  return new Map(
    entryTypes.map((type) => [type.access_entry_type_id, resolveAccessEntryTypeIcon(type)]),
  );
}
